import Compressor from './Compressor';
import Fan from './Fan';
import Filter from './Filter';

export type DefaultValueEntity = {
  name: string;
  value: string;
  description: string;
};

export type AddonCategory = {
  name: string;
  order: string;
  id: string | number;
  addons: Array<AddonOption>;
  is_automatics?: boolean;
};

export type AddonOption = {
  id: number;
  name: string;
  sku: string;
  description: string;
  order: number;
  image?: string;
  category: AddonCategory;
  mandatory?: boolean;
};

export type AggregateCategory = {
  key: string;
  name: string;
  id?: string;
  description?: string;
  order?: number;
  addons?: AddonOption[];
};

export type AggregateCategories = {
  aggregates: Array<Aggregate>;
} & AggregateCategory;

export type GraphCurve = {
  id: number | string;
  color?: string;
  data: { x: number | null; y: number | null }[];
  text?: string;
};

export type HeatReclaim = {
  qv: Array<number>;
  eff: Array<number>;
  qv_def: number;
  eff_def: number;
};

export type ReheaterValues = {
  Kvs: number;
  dp_w: number;
  dp_tot: number;
  W_pump: number;
  N: number;
  standard: boolean;
  key: string;
};

export type OctaveBands = {
  ambient: number[];
  discharge: number[];
  exhaust: number[];
  frequency: number[];
  inlet: number[];
  surface: number[];
  dp_ext_exhaust: number;
  dp_ext_supply: number;
  dp_max_filter_supply: number;
  dp_max_filter_exhaust: number;
  qv_exhaust: number;
  qv_supply: number;
  qv_exhaust_stdair: number;
  qv_supply_stdair: number;
};

export enum StubPlacementOptions {
  A = 'A',
  B = 'B',
  C = 'C',
}

export type StubPlacementOptionNames =
  | 'discharge_air'
  | 'extract_air'
  | 'fresh_air'
  | 'supply_air';

export type StubPlacements = {
  id: string | number;
  sku: string;
  fresh_air: StubPlacementOptions;
  supply_air: StubPlacementOptions;
  extract_air: StubPlacementOptions;
  discharge_air: StubPlacementOptions;
  stub_placement_drawing: string;
  detailed_drawing: string;
  link_to_3d_model: string;
};

export type StubPlacementSet = {
  id: number | string;
  name: string;
  stub_placements: StubPlacements[];
};

export type AirCapacityRating = {
  dirt_level: number;
  dp: number;
  qv: number;
};

export type SystemData = {
  cooling: {
    EER: number;
    Lp: number;
    Qc: number;
    Qe: number;
    Qe_passive: number;
    Te: number;
    Tc: number;
    SFP: number;
    W: number;
    W_fans: number;
    W_rest: number;
    comp_cap: number[];
    etaIs: number;
    etaVol: number;
    solved: boolean;
    exhaust: {
      RH_out: number;
      T_out: number;
      T_wall: number;
    };
    heat_reclaim: number;
    show_heat_reclaim: boolean;
    mDot: number;
    mdot_w: number;
    dp_max_filter_supply: number;
    dp_max_filter_exhaust: number;
    reheater: {
      Kvs: number;
      Q: number;
      RH_out: number;
      T_out: number;
      dp_coil: number;
      dp_valve: number;
      mdot: number;
      valves: ReheaterValues[];
    };
    supply: {
      RH_out: number;
      T_out: number;
      T_wall: number;
    };
    octave_bands: OctaveBands;
    octave_bands_clean: OctaveBands;
  };
  heating: {
    COP: number;
    Lp: number;
    Qc: number;
    Qc_passive: number;
    Qe: number;
    Qe_passive: number;
    SFP: number;
    Te: number;
    Tc: number;
    W: number;
    W_fans: number;
    W_rest: number;
    comp_cap: number[];
    etaIs: number;
    etaVol: number;
    solved: boolean;
    exhaust: {
      RH_out: number;
      T_out: number;
      T_wall: number;
    };
    heat_reclaim: number;
    show_heat_reclaim: boolean;
    heat_reclaim_curve: HeatReclaim;
    mDot: number;
    mdot_w: number;
    dp_max_filter_supply: number;
    dp_max_filter_exhaust: number;
    air_capacity: {
      dp_max: number[];
      qv: number[];
      rating: Array<AirCapacityRating>;
      rating_summer: Array<AirCapacityRating>;
      qv_2_8_ms: number;
      qv_3_2_ms: number;
      qv_fixed_max_dp: number | null;
      sfp1500: number[];
      sfp1800: number[];
      sfp2100: number[];
      sfp2500: number[];
      showRatingSummer: boolean;
    };
    octave_bands: OctaveBands;
    octave_bands_clean: OctaveBands;
    reheater: {
      Kvs: number;
      Q: number;
      RH_out: number;
      T_out: number;
      dp_coil: number;
      dp_valve: number;
      mdot: number;
      valves: ReheaterValues[];
    };
    supply: {
      RH_out: number;
      T_out: number;
      T_wall: number;
    };
  };
  additional: { key: string; value: string }[];
  id: string;
};

export type HeatexRotor = {
  id: string;
  name: string;
  wheel_diameter: number;
  wheel_depth: number;
  install_depth: number;
  install_width: number;
  install_height: number;
};

export type AcousticAttenuation = {
  component: string;
  factors: number[];
};

export type TechnicalSpecification = {
  category: string | number;
  key: string;
  name: string;
  value: string;
  tech_spech_order: number;
};

export type TechnicalSpecificationCategory = {
  id: string | number;
  name: string;
  technical_specifications?: Array<TechnicalSpecification>;
  tech_spech_category_order: number;
};

export type PowerSupplyOption = {
  value: string;
  text: string;
  key: string;
};

export const PowerSupplyOptions: PowerSupplyOption[] = [
  {
    value: 'TWOTHIRTY',
    text: '230 V',
    key: 'TWOTHIRTY',
  },
  {
    value: 'FOURHUNDRED',
    text: '400 V',
    key: 'FOURHUNDRED',
  },
];

export type KVSSizeOption = {
  Kvs: 0.4 | 0.63 | 1.0 | 1.6 | 2.5 | 4.0 | 6.3 | 13 | 16;
  key:
    | 'KVS0_4'
    | 'KVS0_63'
    | 'KVS1_0'
    | 'KVS1_6'
    | 'KVS2_5'
    | 'KVS4_0'
    | 'KVS6_3'
    | 'KVS13'
    | 'KVS16';
};

export const KVSSizeOptions: KVSSizeOption[] = [
  { Kvs: 0.4, key: 'KVS0_4' },
  { Kvs: 0.63, key: 'KVS0_63' },
  { Kvs: 1.0, key: 'KVS1_0' },
  { Kvs: 1.6, key: 'KVS1_6' },
  { Kvs: 2.5, key: 'KVS2_5' },
  { Kvs: 4.0, key: 'KVS4_0' },
  { Kvs: 6.3, key: 'KVS6_3' },
  { Kvs: 13, key: 'KVS13' },
  { Kvs: 16, key: 'KVS16' },
];

export type ElectricReheater = {
  id?: string;
  name: string;
  power_supply: 'FOURHUNDRED' | 'TWOTHIRTY' | 'BOTH';
  capacity: number;
};

export type ReheaterType = {
  key: string;
  value: string;
  text: string;
};

export type AggregateOptions = {
  electric_reheaters: ElectricReheater[];
};

export type AggregateOption = {
  id: string;
  name: string;
  description: string;
  fans: Fan[];
  compressors: Compressor[];
  heatex_rotors: HeatexRotor[];
  supply_filters: Filter[];
  discharge_filters: Filter[];
  electric_reheaters: ElectricReheater[];
  water_reheater: boolean;
  category: AggregateCategory | string;
  min_qv: number;
  power_supply: string;
  calculated?: boolean;
  error?: boolean;
};

export type Aggregate = AggregateOption & {
  var_speed_max_freq: number;
  var_speed_min_freq: number;
  system_data: SystemData;
  addons: AddonOption[];
  stub_placements_left: StubPlacementSet;
  stub_placements_right: StubPlacementSet;
  three_d_model_link: string;
  acoustic_attenuations: AcousticAttenuation[];
  technical_specifications: TechnicalSpecification[];
  technical_specification_categories: TechnicalSpecificationCategory[];
  aggregate_picture: string;
  sku: string;
  supply_filters: Filter[];
  discharge_filters: Filter[];
  control: string;
};

export const hasActive = (aggregate?: Aggregate | null): boolean =>
  !!aggregate &&
  Array.isArray(aggregate.compressors) &&
  aggregate.compressors.length > 0;
